import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, Store, TrendingUp, DollarSign, Package,
  ShoppingCart, AlertTriangle, BarChart3, Calendar,
  Banknote, CreditCard, Eye, X, Printer, ClipboardList,
  FileSpreadsheet, FileText, Download
} from 'lucide-react';
import { toast } from 'sonner';

import { escapeHtml } from '../../utils/security';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_LABELS = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', order: 'Pedido Online' };

const SalesStats = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, logout, getAdminBasePath, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodStats, setPeriodStats] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('day');
  const [loadingPeriod, setLoadingPeriod] = useState(false);
  // Detail modal state
  const [showDetail, setShowDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLabel, setDetailLabel] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [storeName, setStoreName] = useState('Mi Tienda');
  const [exportPeriod, setExportPeriod] = useState('today');
  const [exporting, setExporting] = useState(null);
  const [showVoided, setShowVoided] = useState(false);
  const [voidedSales, setVoidedSales] = useState([]);
  const [loadingVoided, setLoadingVoided] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    loadStats();
    loadPeriodStats('day');
    loadStoreName();
  }, [user, navigate, authLoading, storeDomain, adminPath]);

  const loadStoreName = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setStoreName(res.data?.store_name || 'Mi Tienda');
    } catch (e) {
      console.error('Error loading store name:', e.message);
    }
  };

  const loadVoidedSales = async () => {
    setLoadingVoided(true);
    try {
      const res = await axios.get(`${API}/sales/voided`, { headers: { Authorization: `Bearer ${user.token}` } });
      setVoidedSales(res.data);
      setShowVoided(true);
    } catch (e) { toast.error('Error al cargar ventas anuladas'); }
    finally { setLoadingVoided(false); }
  };

  const formatVoidDate = (d) => {
    if (!d) return '';
    const date = new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d);
    return date.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' });
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await axios.get(`${API}/reports/export-${format}?period=${exportPeriod}`, {
        headers: { Authorization: `Bearer ${user.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `reporte_ventas.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (error) {
      toast.error(`Error al exportar ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/sales`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodStats = async (period) => {
    setLoadingPeriod(true);
    setSelectedPeriod(period);
    try {
      const response = await axios.get(`${API}/sales/stats-by-period?period=${period}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setPeriodStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas por período');
    } finally {
      setLoadingPeriod(false);
    }
  };

  const loadSalesDetail = async (periodKey) => {
    setLoadingDetail(true);
    try {
      const { dateFrom, dateTo } = getDateRange(periodKey, selectedPeriod);
      const response = await axios.get(
        `${API}/sales/detail-by-date?date_from=${dateFrom}&date_to=${dateTo}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setDetailData(response.data);
      setDetailLabel(formatPeriodLabel(periodKey, selectedPeriod));
      setShowDetail(true);
    } catch (error) {
      toast.error('Error al cargar detalle de ventas');
    } finally {
      setLoadingDetail(false);
    }
  };

  const getDateRange = (periodKey, type) => {
    if (type === 'day') {
      return { dateFrom: `${periodKey}T00:00:00`, dateTo: `${periodKey}T23:59:59` };
    }
    if (type === 'week') {
      const [yearStr, weekStr] = periodKey.split('-S');
      const year = parseInt(yearStr);
      const week = parseInt(weekStr);
      const jan1 = new Date(year, 0, 1);
      const dayOfWeek = jan1.getDay() || 7;
      const firstMonday = new Date(jan1);
      firstMonday.setDate(jan1.getDate() + (dayOfWeek <= 1 ? 1 - dayOfWeek : 8 - dayOfWeek));
      const weekStart = new Date(firstMonday);
      weekStart.setDate(firstMonday.getDate() + (week - 1) * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return {
        dateFrom: `${weekStart.toISOString().split('T')[0]}T00:00:00`,
        dateTo: `${weekEnd.toISOString().split('T')[0]}T23:59:59`
      };
    }
    if (type === 'month') {
      const [y, m] = periodKey.split('-');
      const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
      return { dateFrom: `${periodKey}-01T00:00:00`, dateTo: `${periodKey}-${lastDay}T23:59:59` };
    }
    if (type === 'year') {
      return { dateFrom: `${periodKey}-01-01T00:00:00`, dateTo: `${periodKey}-12-31T23:59:59` };
    }
    return { dateFrom: `${periodKey}T00:00:00`, dateTo: `${periodKey}T23:59:59` };
  };

  const handleLogout = () => {
    logout();
    navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
  };

  const periodLabels = { day: 'Día', week: 'Semana', month: 'Mes', year: 'Año' };

  const formatPeriodLabel = (period, type) => {
    if (type === 'day') {
      try {
        const d = new Date(period + 'T00:00:00');
        return d.toLocaleDateString('es-EC', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
      } catch { return period; }
    }
    if (type === 'week') return period.replace('-S', ' Sem ');
    if (type === 'month') {
      try {
        const [y, m] = period.split('-');
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        return `${months[parseInt(m) - 1]} ${y}`;
      } catch { return period; }
    }
    return period;
  };

  const printSaleReceipt = (sale) => {
    const w = window.open('', '_blank', 'width=350,height=700');
    if (!w) { toast.error('Permite ventanas emergentes'); return; }
    const items = sale.items || [];
    const itemsHtml = items.map(i => `
      <tr>
        <td style="text-align:left;padding:4px 0">${escapeHtml(i.product_name || i.name || '')}</td>
        <td style="text-align:center;padding:4px 0">${Number(i.quantity) || 0}</td>
        <td style="text-align:right;padding:4px 0">$${(Number(i.unit_price || i.price) || 0).toFixed(2)}</td>
        <td style="text-align:right;padding:4px 0;font-weight:bold">$${(Number(i.subtotal) || (Number(i.quantity) * Number(i.unit_price || i.price || 0))).toFixed(2)}</td>
      </tr>`).join('');
    const ts = sale.created_at || '';
    const date = new Date(typeof ts === 'string' && !ts.endsWith('Z') ? ts + 'Z' : ts || Date.now());
    const sName = escapeHtml(storeName);
    const cashier = escapeHtml(sale.cashier_name || '');
    const payLabel = escapeHtml(PAYMENT_LABELS[sale.payment_method] || sale.payment_method || '');
    const total = Number(sale.total) || 0;
    const tableName = sale.table_name || (sale.notes && sale.notes.match(/Mesa:\s*(.+?)(\s*\||$)/)?.[1]) || '';
    const isInvoice = sale.invoice_type === 'invoice';
    const accessKey = sale.access_key || '';
    const customerName = sale.customer_name || '';
    const customerIdNum = sale.customer_id_number || '';
    const idTypeLabel = sale.customer_id_type === 'RUC' ? 'RUC' : sale.customer_id_type === 'CEDULA' ? 'Cédula' : 'ID';
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Recibo</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Arial Black', 'Arial', 'Helvetica', sans-serif;
          width: 76mm; margin: 2mm auto; padding: 0;
          color: #000; font-size: 14px; font-weight: 500;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .header { text-align: center; padding-bottom: 6px; }
        .store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; margin-bottom: 4px; text-transform: uppercase; }
        .info { font-size: 13px; color: #000; line-height: 1.6; font-weight: 600; }
        .sep { border: none; border-top: 2px dashed #000; margin: 6px 0; }
        .sep-thick { border: none; border-top: 3px solid #000; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { font-size: 12px; font-weight: 900; text-transform: uppercase; padding: 4px 0; border-bottom: 2px solid #000; }
        td { font-weight: 600; }
        .total-line td { font-size: 24px; font-weight: 900; padding: 6px 0; }
        .pay-line { text-align: center; font-size: 14px; font-weight: 700; margin: 6px 0; padding: 4px 0; border: 1px solid #000; }
        .footer { text-align: center; font-size: 12px; color: #000; margin-top: 8px; line-height: 1.6; font-weight: 600; }
        .access-key { font-size: 9px; word-break: break-all; text-align: center; font-weight: 600; margin: 4px 0; font-family: monospace; letter-spacing: 0.5px; }
        .sri-note { text-align: center; font-size: 10px; font-weight: 600; margin: 4px 0; }
      </style>
    </head><body>
      <div class="header">
        <div class="store-name">${sName}</div>
        ${isInvoice ? '<div class="info" style="font-size:11px;font-weight:900;border:1px solid #000;padding:2px;margin:4px 0">FACTURA ELECTRONICA</div>' : ''}
        <div class="info">${date.toLocaleDateString('es-EC', {day:'2-digit',month:'short',year:'numeric', timeZone:'America/Guayaquil'})} - ${date.toLocaleTimeString('es-EC', {hour:'2-digit',minute:'2-digit', timeZone:'America/Guayaquil'})}</div>
        <div class="info">Cajero: ${cashier}</div>
        ${tableName ? `<div class="info">Mesa: ${escapeHtml(tableName)}</div>` : ''}
        ${isInvoice && customerName ? `<div class="info">Cliente: ${escapeHtml(customerName)}</div>` : ''}
        ${isInvoice && customerIdNum ? `<div class="info">${idTypeLabel}: ${escapeHtml(customerIdNum)}</div>` : ''}
        ${!isInvoice ? '<div class="info" style="font-size:11px">CONSUMIDOR FINAL</div>' : ''}
      </div>
      <hr class="sep">
      <table>
        <thead><tr>
          <th style="text-align:left">Producto</th>
          <th style="text-align:center">Cant</th>
          <th style="text-align:right">P.U.</th>
          <th style="text-align:right">Subt.</th>
        </tr></thead>
        <tbody>${itemsHtml}</tbody>
      </table>
      <hr class="sep">
      <table>
        <tr><td>Subtotal:</td><td style="text-align:right">$${total.toFixed(2)}</td></tr>
      </table>
      <hr class="sep-thick">
      <table><tr class="total-line">
        <td>TOTAL</td>
        <td style="text-align:right">$${total.toFixed(2)}</td>
      </tr></table>
      <div class="pay-line">Pago: ${payLabel}</div>
      ${isInvoice && accessKey ? `
        <hr class="sep">
        <div class="sri-note">CLAVE DE ACCESO:</div>
        <div class="access-key">${escapeHtml(accessKey)}</div>
        <div class="sri-note">Consulte en: https://cel.sri.gob.ec/</div>
      ` : ''}
      <hr class="sep">
      <div class="footer">
        Gracias por su compra<br>
        <strong>${sName}</strong>
      </div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
    w.document.close();
  };

  if (authLoading || loading || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Package size={48} className="animate-spin text-accent-text" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)', overflowX: 'hidden' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <BarChart3 className="text-accent-text flex-shrink-0" size={24} />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-bold truncate">Estadísticas de Ventas</h1>
                <p className="text-xs text-text-muted">Panel de análisis</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => navigate(`${basePath}/dashboard`)} className="btn-secondary text-sm">Productos</button>
              <button onClick={() => navigate(`${basePath}/orders`)} className="btn-secondary text-sm hidden sm:flex">Pedidos</button>
              <button onClick={handleLogout} className="btn-secondary text-sm"><LogOut size={16} /></button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4 md:py-8">
        {/* Export Section */}
        <div className="bg-white p-4 md:p-5 rounded-xl border mb-6" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Download size={20} className="text-accent-text" />
              <h3 className="font-bold text-sm">Exportar Reporte</h3>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <select value={exportPeriod} onChange={e => setExportPeriod(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm flex-1 sm:flex-none"
                style={{ borderColor: 'var(--border-light)' }}
                data-testid="export-period-select">
                <option value="today">Hoy</option>
                <option value="week">Ultimos 7 dias</option>
                <option value="month">Ultimos 30 dias</option>
                <option value="year">Ultimo ano</option>
              </select>
              <button onClick={() => handleExport('excel')} disabled={exporting === 'excel'}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                style={{ background: exporting === 'excel' ? '#9ca3af' : '#10B981' }}
                data-testid="export-excel-btn">
                <FileSpreadsheet size={16} />
                {exporting === 'excel' ? 'Exportando...' : 'Excel'}
              </button>
              <button onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all"
                style={{ background: exporting === 'pdf' ? '#9ca3af' : '#EF4444' }}
                data-testid="export-pdf-btn">
                <FileText size={16} />
                {exporting === 'pdf' ? 'Exportando...' : 'PDF'}
              </button>
              <button onClick={loadVoidedSales} disabled={loadingVoided}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border"
                style={{ borderColor: '#f59e0b', color: '#92400e', background: '#fef3c7' }}
                data-testid="voided-sales-btn">
                <AlertTriangle size={16} />
                {loadingVoided ? '...' : 'Anuladas'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full" style={{ background: 'var(--accent-wash)' }}><DollarSign className="text-accent-text" size={24} /></div>
              <div>
                <p className="text-sm text-text-muted">Ingresos Totales</p>
                <p className="text-2xl font-bold text-accent-text" data-testid="total-revenue">${stats.total_revenue.toFixed(2)}</p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-xs text-text-muted">Pedidos: <span className="font-medium">${(stats.orders_revenue || 0).toFixed(2)}</span></p>
              <p className="text-xs text-text-muted">POS: <span className="font-medium">${(stats.pos_revenue || 0).toFixed(2)}</span></p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full" style={{ background: '#22C55E20' }}><TrendingUp style={{ color: '#22C55E' }} size={24} /></div>
              <div>
                <p className="text-sm text-text-muted">Ganancia Total</p>
                <p className="text-2xl font-bold" style={{ color: '#22C55E' }} data-testid="total-profit">${stats.total_profit.toFixed(2)}</p>
              </div>
            </div>
            <p className="text-xs text-text-muted mt-2">Margen: {stats.profit_margin.toFixed(1)}%</p>
          </div>
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full" style={{ background: '#3B82F620' }}><ShoppingCart style={{ color: '#3B82F6' }} size={24} /></div>
              <div>
                <p className="text-sm text-text-muted">Pedidos</p>
                <p className="text-2xl font-bold" style={{ color: '#3B82F6' }} data-testid="orders-count">{stats.total_orders}</p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-xs text-text-muted">Entregados: <span className="font-medium">{stats.delivered_orders}</span></p>
              <p className="text-xs text-text-muted">Pendientes: <span className="font-medium">{stats.pending_orders}</span></p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full" style={{ background: '#8B5CF620' }}><Store style={{ color: '#8B5CF6' }} size={24} /></div>
              <div>
                <p className="text-sm text-text-muted">Ventas POS</p>
                <p className="text-2xl font-bold" style={{ color: '#8B5CF6' }} data-testid="pos-sales-count">{stats.pos_sales_count || 0}</p>
              </div>
            </div>
            <p className="text-xs text-text-muted mt-2">Costo total: <span className="font-medium">${stats.total_cost.toFixed(2)}</span></p>
          </div>
        </div>

        {/* Period Stats */}
        <div className="bg-white rounded-xl p-4 md:p-6 border mb-8" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Calendar className="text-accent-text" size={20} />
              Ventas por Período
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {['day', 'week', 'month', 'year'].map(p => (
                <button key={p} onClick={() => loadPeriodStats(p)}
                  className={`px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${selectedPeriod === p ? 'text-white' : 'bg-gray-100 text-text-muted hover:bg-gray-200'}`}
                  style={selectedPeriod === p ? { background: 'var(--accent-primary)' } : {}}
                  data-testid={`period-${p}-btn`}>
                  {periodLabels[p]}
                </button>
              ))}
            </div>
          </div>

          {periodStats && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-sm text-green-600 font-medium">Total Ingresos ({periodLabels[selectedPeriod]})</p>
                <p className="text-2xl font-bold text-green-700" data-testid="period-total-revenue">${periodStats.total_revenue.toFixed(2)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-blue-600 font-medium">Total Ganancia ({periodLabels[selectedPeriod]})</p>
                <p className="text-2xl font-bold text-blue-700" data-testid="period-total-profit">${periodStats.total_profit.toFixed(2)}</p>
              </div>
            </div>
          )}

          {loadingPeriod ? (
            <div className="text-center py-8"><Package size={32} className="animate-spin text-accent-text mx-auto" /></div>
          ) : periodStats && periodStats.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <th className="text-left py-3 px-3 font-semibold">Período</th>
                    <th className="text-right py-3 px-3 font-semibold">Total</th>
                    <th className="text-right py-3 px-3 font-semibold">POS</th>
                    <th className="text-right py-3 px-3 font-semibold">Pedidos</th>
                    <th className="text-right py-3 px-3 font-semibold">Ganancia</th>
                    <th className="text-right py-3 px-3 font-semibold"><Banknote size={14} className="inline text-green-600" /> Efect.</th>
                    <th className="text-right py-3 px-3 font-semibold"><CreditCard size={14} className="inline text-blue-600" /> Tarj.</th>
                    <th className="text-right py-3 px-3 font-semibold"><DollarSign size={14} className="inline text-purple-600" /> Transf.</th>
                    <th className="text-center py-3 px-3 font-semibold">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {periodStats.data.map((row, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: 'var(--border-light)' }}>
                      <td className="py-3 px-3 font-medium">{formatPeriodLabel(row.period, selectedPeriod)}</td>
                      <td className="py-3 px-3 text-right font-bold text-accent-text">${row.total_revenue.toFixed(2)}</td>
                      <td className="py-3 px-3 text-right">
                        <span>${row.pos_revenue.toFixed(2)}</span>
                        <span className="text-xs text-text-muted ml-1">({row.pos_count})</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span>${row.orders_revenue.toFixed(2)}</span>
                        <span className="text-xs text-text-muted ml-1">({row.orders_count})</span>
                      </td>
                      <td className="py-3 px-3 text-right font-medium" style={{ color: '#22C55E' }}>${row.profit.toFixed(2)}</td>
                      <td className="py-3 px-3 text-right">${(row.by_payment?.cash || 0).toFixed(2)}</td>
                      <td className="py-3 px-3 text-right">${(row.by_payment?.card || 0).toFixed(2)}</td>
                      <td className="py-3 px-3 text-right">${(row.by_payment?.transfer || 0).toFixed(2)}</td>
                      <td className="py-3 px-3 text-center">
                        <button onClick={() => loadSalesDetail(row.period)}
                          className="p-2 rounded-lg hover:bg-accent-wash transition-colors"
                          title="Ver detalle de ventas"
                          data-testid={`detail-btn-${idx}`}>
                          <Eye size={18} className="text-accent-text" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-text-muted py-8">No hay datos para este período</p>
          )}
        </div>

        {/* Best Sellers & Low Stock */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 border" style={{ borderColor: 'var(--border-light)' }}>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><TrendingUp className="text-accent-text" size={20} />Productos Más Vendidos</h3>
            <div className="space-y-3">
              {stats.best_sellers.length > 0 ? stats.best_sellers.map((product, index) => (
                <div key={`seller-${product.name}-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-section">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold"
                      style={{ background: index === 0 ? 'var(--accent-primary)' : 'var(--accent-wash)', color: index === 0 ? 'white' : 'var(--accent-text)' }}>
                      {index + 1}
                    </div>
                    <div><p className="font-medium text-sm">{product.name}</p><p className="text-xs text-text-muted">{product.quantity} unidades</p></div>
                  </div>
                  <p className="font-bold text-accent-text">${product.revenue.toFixed(2)}</p>
                </div>
              )) : <p className="text-center text-text-muted py-6">No hay ventas aún</p>}
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 border" style={{ borderColor: 'var(--border-light)' }}>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><AlertTriangle style={{ color: '#FFA500' }} size={20} />Productos con Stock Bajo</h3>
            <div className="space-y-3">
              {stats.low_stock_products.length > 0 ? stats.low_stock_products.map((product) => (
                <div key={product.id} className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: '#FFA500', background: '#FFA50010' }}>
                  <p className="font-medium text-sm">{product.name}</p>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ background: '#FFA500', color: 'white' }}>Stock: {product.stock}</span>
                </div>
              )) : <p className="text-center text-text-muted py-6">Todo el inventario está bien abastecido</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Sales Detail Modal */}
      {showDetail && detailData && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowDetail(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="sales-detail-modal">
              <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)', background: 'var(--accent-wash)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClipboardList size={28} className="text-accent-text" />
                    <div>
                      <h2 className="text-xl font-bold">Detalle de Ventas</h2>
                      <p className="text-sm text-text-muted">{detailLabel}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-xs text-text-muted">{detailData.total_count} ventas</p>
                      <p className="font-bold text-accent-text">${detailData.total_revenue.toFixed(2)}</p>
                    </div>
                    <button onClick={() => setShowDetail(false)} className="p-2 hover:bg-white rounded-lg"><X size={20} /></button>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {detailData.sales.length === 0 ? (
                  <div className="text-center py-12">
                    <ClipboardList size={48} className="mx-auto text-text-muted mb-4 opacity-50" />
                    <p className="text-text-muted">No hay ventas para este período</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {detailData.sales.map((sale, idx) => (
                      <div key={sale.id || idx} className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border-light)' }}>
                        <div className="flex items-center justify-between p-4 bg-gray-50">
                          <div className="flex items-center gap-3">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white ${sale.type === 'order' ? 'bg-blue-500' : 'bg-green-500'}`}>
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">
                                {new Date(sale.created_at?.endsWith?.('Z') ? sale.created_at : (sale.created_at + 'Z')).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' })}
                                <span className="text-text-muted ml-2">- {sale.cashier_name}</span>
                                {(sale.table_name || (sale.notes && sale.notes.match(/Mesa:\s*(.+?)(\s*\||$)/)?.[1])) && (
                                  <span className="ml-2 font-medium" style={{ color: 'var(--accent-text)' }}>
                                    · {sale.table_name || sale.notes.match(/Mesa:\s*(.+?)(\s*\||$)/)?.[1]}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-text-muted flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sale.type === 'order' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                                  {sale.type === 'order' ? 'Pedido Online' : 'POS'}
                                </span>
                                {PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                                {(sale.table_name || (sale.notes && sale.notes.match(/Mesa:\s*(.+?)(\s*\||$)/)?.[1])) && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    {sale.table_name || sale.notes.match(/Mesa:\s*(.+?)(\s*\||$)/)?.[1]}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg text-accent-text">${(sale.total || 0).toFixed(2)}</span>
                            <button onClick={() => printSaleReceipt(sale)}
                              className="p-2 hover:bg-white rounded-lg transition-colors" title="Imprimir recibo"
                              data-testid={`print-detail-${idx}`}>
                              <Printer size={18} className="text-text-muted" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-text-muted text-xs">
                                <th className="text-left pb-2">Producto</th>
                                <th className="text-center pb-2">Cant.</th>
                                <th className="text-right pb-2">P.Unit.</th>
                                <th className="text-right pb-2">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(sale.items || []).map((item, j) => (
                                <tr key={`item-${j}-${item.product_name || item.name}`} className="border-t" style={{ borderColor: 'var(--border-light)' }}>
                                  <td className="py-2 text-left">{item.product_name || item.name}</td>
                                  <td className="py-2 text-center">{item.quantity}</td>
                                  <td className="py-2 text-right">${(item.unit_price || item.price || 0).toFixed(2)}</td>
                                  <td className="py-2 text-right font-medium">${(item.subtotal || (item.quantity * (item.unit_price || item.price || 0))).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Voided Sales Modal */}
      {showVoided && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowVoided(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="voided-sales-modal">
              <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)', background: '#fef3c7' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle size={28} className="text-amber-600" />
                    <div>
                      <h2 className="text-xl font-bold text-amber-900">Ventas Anuladas</h2>
                      <p className="text-sm text-amber-700">Registro de auditoría</p>
                    </div>
                  </div>
                  <button onClick={() => setShowVoided(false)} className="p-2 hover:bg-amber-100 rounded-lg"><X size={20} /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {voidedSales.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle size={48} className="mx-auto text-text-muted mb-4 opacity-30" />
                    <p className="text-text-muted">No hay ventas anuladas</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {voidedSales.map((v, idx) => (
                      <div key={v.id || idx} className="border rounded-xl overflow-hidden" style={{ borderColor: '#fcd34d' }}>
                        <div className="flex items-center justify-between p-4" style={{ background: '#fffbeb' }}>
                          <div>
                            <p className="font-bold text-sm text-amber-900">
                              Venta anulada — ${(v.total || 0).toFixed(2)}
                            </p>
                            <p className="text-xs text-amber-700">
                              Anulada por: <strong>{v.voided_by}</strong> — {formatVoidDate(v.voided_at)}
                            </p>
                            <p className="text-xs text-text-muted mt-0.5">
                              Venta original: {formatVoidDate(v.original_date)} · {PAYMENT_LABELS[v.payment_method] || v.payment_method} · Cajero: {v.cashier_name || '-'}
                            </p>
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">ANULADA</span>
                        </div>
                        <div className="p-3">
                          <table className="w-full text-xs">
                            <thead><tr className="text-text-muted"><th className="text-left pb-1">Producto</th><th className="text-center pb-1">Cant</th><th className="text-right pb-1">Subtotal</th></tr></thead>
                            <tbody>
                              {(v.items || []).map((item, j) => (
                                <tr key={j} className="border-t" style={{ borderColor: '#fef3c7' }}>
                                  <td className="py-1">{item.product_name}</td>
                                  <td className="py-1 text-center">{item.quantity}</td>
                                  <td className="py-1 text-right font-medium">${(item.subtotal || 0).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                <p className="text-center text-sm text-text-muted">
                  Total anuladas: <strong className="text-amber-700">{voidedSales.length}</strong> — 
                  Monto: <strong className="text-red-600">${voidedSales.reduce((a, v) => a + (v.total || 0), 0).toFixed(2)}</strong>
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesStats;
